/** Without `canplay` or a final source error by then, the play button shows anyway (data saver, stalled network). */
const VIDEO_LOAD_TIMEOUT = 15000;

/**
 * Product page media area: switches between the image gallery, the product video and the
 * 3D viewer. The server renders the gallery as the active panel, so without this script the
 * gallery stays usable and the tabs are simply inert.
 *
 * Markup contract (sections/main-product.liquid):
 * - root `<product-media data-media="gallery">`
 * - tabs `[data-media-tab="gallery|video|3d"]` with `aria-pressed`
 * - panels `[data-media-panel="video|3d"]`; the gallery is the `product-gallery` element
 * - video poster button `[data-video-placeholder]` inside the video panel
 * - video panel `data-video-state`: idle -> loading -> ready | error. The video only starts
 *   downloading once its tab is opened, and the play button shows once it can play, its last
 *   source fails or VIDEO_LOAD_TIMEOUT passes. The poster can be clicked at any time.
 * - 3D panel: the viewer (snippets/model-3d-view.liquid) starts loading on the first
 *   `model-3d-resize` event, which this element dispatches when the 3D tab opens.
 * - SKU mode (`data-media-by-variant`, with `form` and `data-section-id`): the images, video
 *   and 3D belong to the selected variant (blocks/_product-media.liquid). On the product form's variant:change
 *   the element fetches the section for the new variant and swaps in its media; only the latest request applies.
 */
class ProductMedia extends HTMLElement {
  #abortController;
  #resizeFrame = 0;
  #sizeObserver;
  #videoLoadController;
  #videoLoadTimer = 0;
  #mediaRequest = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
    // The product form dispatches variant:change (detail { product, variant, previousVariant }) and it bubbles.
    if (this.hasAttribute('data-media-by-variant')) {
      document.addEventListener('variant:change', this.#onVariantChange, { signal: this.#abortController.signal });
    }

    // Pad centres the sticky media in the screen (assets/component-product-media.css), which needs its height.
    this.#sizeObserver = new ResizeObserver(([entry]) => {
      const blockSize = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      this.style.setProperty('--product-media-block-size', `${Math.round(blockSize)}px`);
    });
    this.#sizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#sizeObserver?.disconnect();
    this.#abortController?.abort();
    this.#mediaRequest?.abort();
    this.#mediaRequest = null;
    // Drop the pending load listeners and timeout; let a reconnect start the load again.
    this.#stopVideoLoad();
    if (this.#videoPanel?.dataset.videoState === 'loading') this.#setVideoState('idle');
    if (this.#resizeFrame) cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = 0;
  }

  get #video() {
    return this.querySelector('[data-media-panel="video"] video');
  }

  get #videoPlaceholder() {
    return this.querySelector('[data-video-placeholder]');
  }

  get #videoPanel() {
    return this.querySelector('[data-media-panel="video"]');
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-media-tab]');
    if (tab && this.contains(tab)) {
      this.select(tab.dataset.mediaTab);
      return;
    }

    const placeholder = event.target.closest('[data-video-placeholder]');
    if (placeholder && this.contains(placeholder)) {
      this.#playVideo();
    }
  };

  #onVariantChange = async (event) => {
    // getAttribute: on a product form, `form.id` is the variant input named "id", not the form's ID.
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.getAttribute('form')) return;
    const variantId = event.detail?.variant?.id;
    if (variantId == null) return;

    this.#mediaRequest?.abort();
    const request = (this.#mediaRequest = new AbortController());
    // The current page URL keeps `view` (alternate templates) and other parameters, so the section is rendered from
    // the template on screen rather than the product's default one.
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      if (request.signal.aborted) return;

      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('product-media');
      if (next) this.#replaceMedia(next);
    } catch (error) {
      // Keep the current media when the request fails; a later variant change tries again.
      if (error.name !== 'AbortError') console.error('Product media: variant media failed to load', error);
    } finally {
      if (this.#mediaRequest === request) this.#mediaRequest = null;
    }
  };

  /** Swaps in the media rendered for the new variant and reopens the panel the visitor had open, if it still exists. */
  #replaceMedia(next) {
    const openPanel = this.dataset.media;
    this.#resetVideo();
    this.#stopVideoLoad();

    // The page already runs the viewer and gallery scripts; scripts inside parsed HTML would not run anyway.
    next.querySelectorAll('script').forEach((script) => script.remove());
    this.dataset.media = 'gallery';
    this.replaceChildren(...next.childNodes);
    this.select(openPanel);
  }

  /**
   * Show one media panel. Unknown or missing panels fall back to the gallery.
   * @param {string} type - "gallery", "video" or "3d"
   */
  select(type) {
    const nextType = this.#hasPanel(type) ? type : 'gallery';
    if (this.dataset.media === nextType) return;

    this.dataset.media = nextType;
    this.querySelectorAll('[data-media-tab]').forEach((tab) => {
      tab.setAttribute('aria-pressed', String(tab.dataset.mediaTab === nextType));
    });

    this.#resetVideo();

    if (nextType === 'video') this.#loadVideo();

    if (nextType === '3d') {
      // The 3D canvas measures its container, which only has a size once the panel is shown.
      if (this.#resizeFrame) cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = requestAnimationFrame(() => {
        this.#resizeFrame = 0;
        window.dispatchEvent(new CustomEvent('model-3d-resize'));
      });
    }
  }

  #hasPanel(type) {
    if (type === 'gallery') return true;
    return Boolean(this.querySelector(`[data-media-panel="${CSS.escape(type)}"]`));
  }

  #resetVideo() {
    const video = this.#video;
    if (!video) return;

    video.pause();
    video.currentTime = 0;
    if (this.#videoPlaceholder) this.#videoPlaceholder.hidden = false;
  }

  #setVideoState(state) {
    const panel = this.#videoPanel;
    if (!panel) return;
    panel.dataset.videoState = state;
    panel.toggleAttribute('aria-busy', state === 'loading');
  }

  /** Start downloading the video the first time its tab is opened. */
  #loadVideo() {
    const video = this.#video;
    const panel = this.#videoPanel;
    if (!video || !panel || panel.dataset.videoState !== 'idle') return;

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      this.#setVideoState('ready');
      return;
    }

    this.#setVideoState('loading');
    this.#videoLoadController = new AbortController();
    const { signal } = this.#videoLoadController;
    video.addEventListener('canplay', () => this.#finishVideoLoad('ready'), { signal });
    // video_tag renders <source> children: a failed source fires a non-bubbling `error` on that
    // source (caught in the capture phase); only the last one means no source can play. Errors from
    // other descendants (the fallback <img>) are ignored.
    video.addEventListener(
      'error',
      (event) => {
        const { target } = event;
        if (target instanceof HTMLSourceElement) {
          if (!target.matches(':last-of-type')) return;
        } else if (target !== video) {
          return;
        }
        this.#finishVideoLoad('error');
      },
      { capture: true, signal }
    );
    this.#videoLoadTimer = window.setTimeout(() => this.#finishVideoLoad('error'), VIDEO_LOAD_TIMEOUT);
    video.preload = 'auto';
    video.load();
  }

  #finishVideoLoad(state) {
    this.#stopVideoLoad();
    this.#setVideoState(state);
  }

  #stopVideoLoad() {
    this.#videoLoadController?.abort();
    this.#videoLoadController = null;
    window.clearTimeout(this.#videoLoadTimer);
    this.#videoLoadTimer = 0;
  }

  #playVideo() {
    const video = this.#video;
    if (!video) return;

    // A click while the video is still loading plays it too; the native controls show the buffering.
    this.#videoPlaceholder.hidden = true;
    video.closest('video-media')?.setAttribute('loaded', '');
    video.currentTime = 0;

    const playback = video.play();
    playback?.catch(() => {
      // Autoplay can be refused (e.g. low-power mode); keep the native controls reachable.
    });

    // Small screens play the video fullscreen, matching the previous behaviour.
    if (window.matchMedia('(max-width: 699px)').matches) {
      if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      } else {
        video.requestFullscreen?.().catch(() => {});
      }
    }
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia);
}
